import React, { forwardRef } from "react";
import { Field } from "@base-ui/react/field";
import { Radio } from "@base-ui/react/radio";
import c from "classnames/bind";

import { useRadioButtonGroupContext } from "./radio-button-group.context";
import styles from "./radio-button-group-item.module.scss";

const cx = c.bind(styles);

export type RadioButtonGroupItem = {
  ["data-testid"]?: string;
  disabled?: boolean;
  id: string;
  label: string;
  value: string;
  helperText?: string;
  className?: string;
};

const rootClassName = "purpur-radio-button-group-item";

export const RadioButtonGroupItem = forwardRef<HTMLSpanElement, RadioButtonGroupItem>(
  ({ disabled, id, value, label, "data-testid": dataTestId, helperText, className }, ref) => {
    const context = useRadioButtonGroupContext();
    const resolvedDisabled = disabled || context.disabled;

    return (
      <Field.Item
        disabled={resolvedDisabled}
        className={cx(className, `${rootClassName}`)}
        data-testid={dataTestId}
        key={`radio-item-${value}`}
      >
        <Field.Label
          data-testid={dataTestId ? `${dataTestId}-label` : undefined}
          className={cx(`${rootClassName}__label`)}
          data-negative={context.negative}
        >
          <span className={cx(`${rootClassName}__radio-container`)}>
            <Radio.Root
              className={cx(`${rootClassName}__radio`)}
              data-testid={dataTestId ? `${dataTestId}-radio` : undefined}
              data-negative={context.negative}
              id={id}
              ref={ref}
              value={value}
            >
              <Radio.Indicator
                className={cx(`${rootClassName}__radio-indicator`)}
                keepMounted
                data-negative={context.negative}
              />
            </Radio.Root>
          </span>
          {label}
        </Field.Label>
        {helperText && (
          <Field.Description
            data-testid={dataTestId && `${dataTestId}-helper-text`}
            data-negative={context.negative}
            className={cx(`${rootClassName}__description`)}
            dir={context.dir}
          >
            {helperText}
          </Field.Description>
        )}
      </Field.Item>
    );
  }
);

RadioButtonGroupItem.displayName = "RadioButtonGroupItem";
